import NotesManager from "../HubspotIntegration/NotesManager";
import TicketManager from "../HubspotIntegration/TicketsManager";
import randomHashGenerator from "../helpers/randomHashGenerator";
import { HubspotTicketStatus, HSTicket, } from "../types/serviceProgramDatabase";
import TicketQueue from "../utils/ServiceProgramTicketsQueue";
import { Database } from "./Database";
export default class ServiceProgramDatabase extends Database {
    constructor() {
        super(...arguments);
        this.REGISTERED_SERIAL_NUMBERS_PATH = 'registered-serial-numbers';
        this.REGISTERED_USERS_PATH = 'registered-users';
        this.REGISTERED_USERS_SERIAL_NUMBERS_PATH = 'serialNumbers';
        this.REGISTERED_USERS_STATS_PATH = 'stats';
        this.REGISTERED_USERS_REPORTS_PATH = 'reports';
        this.hubspotTicketManager = new TicketManager();
        this.hubspotNoteManager = new NotesManager();
        this.ticketsQueue = new TicketQueue();
        //////////
    }
    addDataToSession(key, value) {
        throw new Error("Irrelevant inherited method" + key + value);
    }
    // Serial number 
    async isSerialNumberRegistered(serialNumber) {
        return this.exists(this.REGISTERED_SERIAL_NUMBERS_PATH + "/" + serialNumber);
    }
    async unregisterSerialNumberFromUser(email, serialNumber) {
        var _a;
        // this function removes the serial number from the user, and also removes the reports for this serial number
        const lowerCaseEmail = email.toLowerCase();
        // first get user to get serial numbers and reports
        // then remove serial numbers and reports
        const user = await this.getUser(lowerCaseEmail);
        if (!user) {
            return false;
        }
        const serialNumbers = Object.keys(user.serialNumbers);
        const isOnly1SerialNumber = serialNumbers.length === 1;
        if (isOnly1SerialNumber) {
            return this.removeRegisteredUser(lowerCaseEmail);
        }
        const serialNumberToBeRemoved = serialNumbers.find((SN) => SN === serialNumber);
        const promises = [];
        if (serialNumberToBeRemoved) {
            const reports = Object.keys(((_a = user.serialNumbers[serialNumberToBeRemoved]) === null || _a === void 0 ? void 0 : _a.reports) || {});
            for (const reportId of reports) {
                promises.push(this.unregisterReport(lowerCaseEmail, serialNumberToBeRemoved, reportId));
            }
            promises.push(new Promise(resolve => setTimeout(resolve, 200))); // wait 200ms to make sure the reports are removed
            promises.push(this.removeRegisteredSerialNumber(serialNumberToBeRemoved));
            promises.push(this.removeSerialNumberFromUser(lowerCaseEmail, serialNumberToBeRemoved));
        }
        try {
            await Promise.all(promises);
        }
        catch (error) {
            console.error(error);
            return false;
        }
        return true;
    }
    async registerSerialNumber(serialNumber, email) {
        const data = {
            serialNumber: serialNumber,
            date: new Date().toISOString(),
            email: email
        };
        return this.set(data, this.REGISTERED_SERIAL_NUMBERS_PATH + "/" + serialNumber);
    }
    async registerSerialNumbers(serialNumbers, email) {
        const lowerCaseEmail = email.toLowerCase();
        const promises = serialNumbers.map((serialNumber) => this.registerSerialNumber(serialNumber, lowerCaseEmail));
        try {
            await Promise.all(promises);
        }
        catch (error) {
            console.error(error);
            return false;
        }
        return true;
    }
    getSerialNumber(serialNumber) {
        return this.get(this.REGISTERED_SERIAL_NUMBERS_PATH + "/" + serialNumber);
    }
    async getEmailGivenSerialNumber(serialNumber) {
        const serialNumberData = await this.getSerialNumber(serialNumber);
        if (!serialNumberData) {
            return null;
        }
        return serialNumberData.email;
    }
    removeRegisteredSerialNumber(serialNumber) {
        return this.remove(this.REGISTERED_SERIAL_NUMBERS_PATH + "/" + serialNumber);
    }
    removeSerialNumberFromUser(email, serialNumber) {
        const lowerCaseEmail = email.toLowerCase();
        const urlEncodedEmail = ServiceProgramDatabase.encodeEmail(lowerCaseEmail);
        return this.remove(this.REGISTERED_USERS_PATH + "/" + urlEncodedEmail + "/" + this.REGISTERED_USERS_SERIAL_NUMBERS_PATH + "/" + serialNumber);
    }
    ////////////////
    // User
    async registerUser(email, establishment, serialNumbers) {
        const lowerCaseEmail = email.toLowerCase();
        const urlEncodedEmail = ServiceProgramDatabase.encodeEmail(lowerCaseEmail);
        const date = new Date().toISOString();
        const didRegisterSerialNumbers = await this.registerSerialNumbers(serialNumbers, lowerCaseEmail);
        if (!didRegisterSerialNumbers) {
            return false;
        }
        const serialNumbersObject = {};
        serialNumbers.forEach((serialNumber) => {
            serialNumbersObject[serialNumber] = { serialNumber, date };
        });
        const data = {
            email: lowerCaseEmail,
            establishment: establishment,
            [this.REGISTERED_USERS_SERIAL_NUMBERS_PATH]: serialNumbersObject,
            date: date
        };
        const didRegisterUser = await this.set(data, this.REGISTERED_USERS_PATH + "/" + urlEncodedEmail);
        if (!didRegisterUser) {
            // remove serial numbers
            serialNumbers.forEach((serialNumber) => {
                this.removeRegisteredSerialNumber(serialNumber);
            });
            return false;
        }
        return true;
    }
    getUser(email) {
        const lowerCaseEmail = email.toLowerCase();
        const urlEncodedEmail = ServiceProgramDatabase.encodeEmail(lowerCaseEmail);
        return this.get(this.REGISTERED_USERS_PATH + "/" + urlEncodedEmail);
    }
    getUsers() {
        return this.get(this.REGISTERED_USERS_PATH);
    }
    async removeRegisteredUser(email) {
        const lowerCaseEmail = email.toLowerCase();
        const urlEncodedEmail = ServiceProgramDatabase.encodeEmail(lowerCaseEmail);
        // first get user to get serial numbers and reports
        // then remove serial numbers and reports
        // then remove user
        const user = await this.getUser(lowerCaseEmail);
        if (!user) {
            return false;
        }
        const serialNumbers = Object.keys(user.serialNumbers);
        const promises = serialNumbers.map((serialNumber) => this.removeRegisteredSerialNumber(serialNumber));
        for (const serialNumber of serialNumbers) {
            const reports = Object.keys(user.serialNumbers[serialNumber].reports || {});
            for (const reportId of reports) {
                promises.push(this.unregisterReport(lowerCaseEmail, serialNumber, reportId));
            }
        }
        try {
            await Promise.all(promises);
        }
        catch (error) {
            console.error(error);
            return false;
        }
        return this.remove(this.REGISTERED_USERS_PATH + "/" + urlEncodedEmail);
    }
    addSerialNumberToUser(email, serialNumber) {
        const lowerCaseEmail = email.toLowerCase();
        const urlEncodedEmail = ServiceProgramDatabase.encodeEmail(lowerCaseEmail);
        const date = new Date().toISOString();
        const data = {
            serialNumber: serialNumber,
            date: date
        };
        return this.set(data, this.REGISTERED_USERS_PATH + "/" + urlEncodedEmail + "/" + this.REGISTERED_USERS_SERIAL_NUMBERS_PATH + "/" + serialNumber);
    }
    async appendSerialNumbersToUser(email, serialNumbers) {
        const lowerCaseEmail = email.toLowerCase();
        try {
            const promises = serialNumbers.map((serialNumber) => this.addSerialNumberToUser(lowerCaseEmail, serialNumber));
            const didAddSNToUser = await Promise.all(promises);
            if (!didAddSNToUser.every((didAdd) => didAdd)) {
                return false;
            }
            return this.registerSerialNumbers(serialNumbers, lowerCaseEmail);
        }
        catch (error) {
            console.error(error);
            return false;
        }
    }
    async addMartyNameToSerialNumber(serialNumber, name) {
        const userEmail = await this.getEmailGivenSerialNumber(serialNumber);
        if (!userEmail) {
            return false;
        }
        const lowerCaseEmail = userEmail.toLowerCase();
        const urlEncodedEmail = ServiceProgramDatabase.encodeEmail(lowerCaseEmail);
        const data = {
            name: name
        };
        return this.update(data, this.REGISTERED_USERS_PATH + "/" + urlEncodedEmail + "/" + this.REGISTERED_USERS_SERIAL_NUMBERS_PATH + "/" + serialNumber);
    }
    ////////////
    // Login token
    async addLoginTokenToUser(email) {
        const lowerCaseEmail = email.toLowerCase();
        const urlEncodedEmail = ServiceProgramDatabase.encodeEmail(lowerCaseEmail);
        const loginToken = this.generateLoginToken(urlEncodedEmail);
        const didSet = await this.set(loginToken, this.REGISTERED_USERS_PATH + "/" + urlEncodedEmail + "/loginToken");
        if (didSet) {
            return loginToken;
        }
        return false;
    }
    async getLoginToken(email) {
        const lowerCaseEmail = email.toLowerCase();
        const urlEncodedEmail = ServiceProgramDatabase.encodeEmail(lowerCaseEmail);
        const loginToken = await this.get(this.REGISTERED_USERS_PATH + "/" + urlEncodedEmail + "/loginToken");
        if (loginToken) {
            const expirationDate = new Date(loginToken.expirationDate);
            if (expirationDate < new Date()) {
                return undefined;
            }
        }
        return loginToken;
    }
    ////////////
    // Reports
    /**
     *
     * @info
     * Database "Reports" are Hubspot "Notes"
     */
    async addReportToUser(email, serialNumber, report) {
        const lowerCaseEmail = email.toLowerCase();
        const urlEncodedEmail = ServiceProgramDatabase.encodeEmail(lowerCaseEmail);
        return this.set(report, this.REGISTERED_USERS_PATH + "/" + urlEncodedEmail + "/" + this.REGISTERED_USERS_SERIAL_NUMBERS_PATH + "/" + serialNumber + "/" + this.REGISTERED_USERS_REPORTS_PATH + "/" + report.id);
    }
    removeReportFromUser(email, serialNumber, reportId) {
        const lowerCaseEmail = email.toLowerCase();
        const urlEncodedEmail = ServiceProgramDatabase.encodeEmail(lowerCaseEmail);
        return this.remove(this.REGISTERED_USERS_PATH + "/" + urlEncodedEmail + "/" + this.REGISTERED_USERS_SERIAL_NUMBERS_PATH + "/" + serialNumber + "/" + this.REGISTERED_USERS_REPORTS_PATH + "/" + reportId);
    }
    async unregisterReport(email, serialNumber, reportId) {
        const lowerCaseEmail = email.toLowerCase();
        // first we need to fetch the report to get the hubspot ticket and note id
        const ticket = await this.getReport(reportId, lowerCaseEmail, serialNumber);
        if (!ticket) {
            return false;
        }
        const hubspotNoteId = ticket.hubspotNoteId;
        const hubspotTicketId = ticket.hubspotTicketId;
        const removedFromUser = await this.removeReportFromUser(lowerCaseEmail, serialNumber, reportId);
        if (!removedFromUser) {
            return false;
        }
        if (hubspotNoteId) {
            const deletedNote = await this.hubspotNoteManager.deleteNote(hubspotNoteId);
            if (!deletedNote) {
                return false;
            }
        }
        if (hubspotTicketId) {
            const deletedTicket = await this.hubspotTicketManager.deleteTicket(hubspotTicketId);
            if (!deletedTicket) {
                return false;
            }
        }
        return true;
    }
    async registerReport(email, serialNumber, reportTitle, reportDescription) {
        const ticketId = randomHashGenerator(10);
        this.ticketsQueue.addTicket({
            email,
            serialNumber,
            reportTitle,
            reportDescription,
            ticketId: ticketId
        });
        await this.ticketsQueue.processTicketsQueue(this.processTicketQueue.bind(this));
        return new Promise((resolve, reject) => {
            // we need to wait for the ticket to be processed
            // and get the result from the results queue
            const TIMEOUT_TIME = 20000; // if we don't get a result in 10 seconds, we reject
            const beginTime = new Date().getTime();
            const interval = setInterval(() => {
                const result = this.ticketsQueue.getResultByTicketId(ticketId);
                if (result === -1) {
                    return;
                }
                if (beginTime + TIMEOUT_TIME < new Date().getTime()) {
                    reject("Timeout");
                    clearInterval(interval);
                    return;
                }
                resolve(result);
                clearInterval(interval);
                return;
            }, 500);
        });
    }
    async processTicketQueue(email, serialNumber, reportTitle, reportDescription) {
        /**
         * Explanation: this function first checks if the user and serial number exist
         * then it checks if the user already has a report for this serial number
         * if it does, it checks if there is a report with the same title
         *     if there is, it checks if the ticket is closed or not
         *        if it is closed, it creates a new report and note and adds the note to the ticket and updates the ticket status to open
         *        if it is open, it returns false
         * if it doesn't, it creates a new ticket and note in hubspot
         * then it adds the report to the user
         */
        // pausing for a random amount of time between 0 and 5 seconds to avoid duplicate tickets
        const randomTime = Math.floor(Math.random() * 5000);
        await new Promise(resolve => setTimeout(resolve, randomTime));
        const user = await this.checkExistenceOfUserAndSerial(email, serialNumber);
        if (!user) {
            return false;
        }
        const serialNumberReports = Object.values(user.serialNumbers[serialNumber].reports || {});
        const dbReportId = randomHashGenerator(20);
        let hubspotTicketId;
        let hubspotNoteId;
        if (serialNumberReports.length > 0) { // there is at least one report for this serial number, meaning that hubspot has already a ticket for this serial number
            const reportsWithSameTitle = this.getReportsWithSameTitle(serialNumberReports, reportTitle);
            if (reportsWithSameTitle.length > 0) {
                console.info(`User ${email.toLowerCase()} already has a report with the same title ${reportTitle}`);
                // at this point we have at least one report with the same title
                // we need to check if the hubspot ticket is closed or not
                // if it is closed, we need to create a new report and note
                // and add the note to the ticket and update the ticket status to open
                // if it is open, we return false
                const hubspotTicketStatus = await this.hubspotTicketManager.getTicketStatus(serialNumberReports[0].hubspotTicketId, email.toLowerCase());
                if (!hubspotTicketStatus) {
                    console.error("Hubspot ticket status was not found");
                    return false;
                }
                // if the ticket is open or in progress, we don't create a new report and return false
                if (hubspotTicketStatus === HubspotTicketStatus.OPEN ||
                    hubspotTicketStatus === HubspotTicketStatus.IN_PROGRESS) {
                    return false;
                }
            }
            hubspotTicketId = serialNumberReports[0].hubspotTicketId;
            hubspotNoteId = await this.hubspotNoteManager.createNote(reportTitle, serialNumberReports[0].hubspotTicketId, dbReportId, reportDescription, email.toLowerCase(), serialNumber);
            await this.updateHubspotTicketStatus(serialNumberReports[0].id, email.toLowerCase(), serialNumber, HubspotTicketStatus.OPEN);
        }
        else { // there are no reports for this serial number, create a new ticket and note in hubspot
            hubspotTicketId = await this.createNewHubspotTicket(reportDescription, dbReportId, email.toLowerCase(), serialNumber);
            if (!hubspotTicketId) {
                return false;
            }
            hubspotNoteId = await this.hubspotNoteManager.createNote(reportTitle, hubspotTicketId, dbReportId, reportDescription, email.toLowerCase(), serialNumber);
        }
        if (!hubspotNoteId) {
            await this.hubspotTicketManager.deleteTicket(hubspotTicketId);
            return false;
        }
        const date = new Date().toISOString();
        const report = {
            id: dbReportId,
            title: reportTitle,
            description: reportDescription,
            date,
            email: email.toLowerCase(),
            serialNumber,
            hubspotTicketId,
            hubspotNoteId
        };
        const addedToUser = await this.addReportToUser(email.toLowerCase(), serialNumber, report);
        if (!addedToUser) {
            await this.hubspotTicketManager.deleteTicket(hubspotTicketId);
            await this.hubspotNoteManager.deleteNote(hubspotNoteId);
            return false;
        }
        return dbReportId;
    }
    async updateHubspotTicketStatus(reportId, email, serialNumber, status) {
        // ticket status only exists in the hubspot ticket
        // first we need to fetch the report to get the hubspot ticket id
        const report = await this.getReport(reportId, email, serialNumber);
        if (!report) {
            return false;
        }
        const hubspotTicketId = report.hubspotTicketId;
        const didUpdateOnHs = !!await this.hubspotTicketManager.updateTicketStatus(hubspotTicketId, status);
        return didUpdateOnHs;
    }
    async getReport(reportId, email, serialNumber) {
        const lowerCaseEmail = email.toLowerCase();
        // first we get the report from the db and then we fetch the hubspot ticket to get the status
        const urlEncodedEmail = ServiceProgramDatabase.encodeEmail(lowerCaseEmail);
        const report = await this.get(this.REGISTERED_USERS_PATH + "/" + urlEncodedEmail + "/" + this.REGISTERED_USERS_SERIAL_NUMBERS_PATH + "/" + serialNumber + "/" + this.REGISTERED_USERS_REPORTS_PATH + "/" + reportId);
        if (!report) {
            return null;
        }
        // commented out because we don't need to fetch the ticket status anymore
        // const hubspotTicketId = report.hubspotTicketId;
        // const hubspotTicket = await this.hubspotTicketManager.getTicket(hubspotTicketId, lowerCaseEmail);
        // if (!hubspotTicket) {
        //     console.debug("hubspotTicket was not found, id:", report.hubspotTicketId);
        //     return null;
        // }
        // report.status = (HubspotTicketStatusMap[hubspotTicket.properties.hs_pipeline_stage as keyof typeof HubspotTicketStatusMap] || HubspotTicketStatus.IN_PROGRESS) as HubspotTicketStatus;
        return report;
    }
    //////////
    // HELPERS
    static encodeEmail(email) {
        if (ServiceProgramDatabase.isEmailEncoded(email)) {
            return email;
        }
        return encodeURIComponent(email).replaceAll(".", "%2E");
    }
    static decodeEmail(email) {
        if (!ServiceProgramDatabase.isEmailEncoded(email)) {
            return email;
        }
        return decodeURIComponent(email.replaceAll("%2E", "."));
    }
    static isEmailEncoded(email) {
        return email.includes("%2E");
    }
    generateLoginToken(encodedEmail) {
        const lowerCaseEmail = ServiceProgramDatabase.decodeEmail(encodedEmail);
        const token = randomHashGenerator(20);
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + 1);
        const query = `?email=${lowerCaseEmail}&token=${token}`;
        return {
            queryString: query,
            token: token,
            expirationDate: expirationDate.toISOString()
        };
    }
    // Function to check if user and serial number exist
    async checkExistenceOfUserAndSerial(email, serial) {
        const lowerCaseEmail = email.toLowerCase();
        const user = await this.getUser(lowerCaseEmail);
        if (!user || !user.serialNumbers[serial]) {
            return null;
        }
        return user;
    }
    getReportsWithSameTitle(reports, title) {
        const reportsWithSameTitle = reports.filter((report) => report.title === title);
        return reportsWithSameTitle;
    }
    ////// HUBSPOT INTEGRATION //////
    async createNewHubspotTicket(description, id, email, serial) {
        return await this.hubspotTicketManager.createTicket(description, id, email, serial);
    }
}
export async function getHSTicketsFromReports(reports) {
    const hsTicketIds = [];
    const userEmails = [];
    for (const reportId in reports) {
        const report = reports[reportId];
        const hsTicketId = report.hubspotTicketId;
        if (hsTicketId && !hsTicketIds.includes(hsTicketId)) {
            userEmails.push(report.email);
            hsTicketIds.push(hsTicketId);
        }
    }
    const hsTickets = [];
    const ticketManager = new TicketManager();
    const promises = hsTicketIds.map((hsTicketId) => ticketManager.getTicket(hsTicketId, userEmails[hsTicketIds.indexOf(hsTicketId)]));
    const tickets = await Promise.all(promises);
    for (const ticket of tickets) {
        if (ticket) {
            hsTickets.push(new HSTicket(ticket));
        }
    }
    return hsTickets;
}
